import { authFetch } from '../../utils/authFetch';
// reviewActions.js
// Redux actions for managing assessment reviews

// Action types
export const FETCH_REVIEWS_START = 'FETCH_REVIEWS_START';
export const FETCH_REVIEWS_SUCCESS = 'FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_FAILURE = 'FETCH_REVIEWS_FAILURE';
export const SUBMIT_FOR_REVIEW = 'SUBMIT_FOR_REVIEW';
export const SUBMIT_FOR_REVIEW_SUCCESS = 'SUBMIT_FOR_REVIEW_SUCCESS';
export const SUBMIT_FOR_REVIEW_FAILURE = 'SUBMIT_FOR_REVIEW_FAILURE';
export const UPDATE_REVIEW_STATUS = 'UPDATE_REVIEW_STATUS';
export const UPDATE_REVIEW_STATUS_SUCCESS = 'UPDATE_REVIEW_STATUS_SUCCESS';
export const UPDATE_REVIEW_STATUS_FAILURE = 'UPDATE_REVIEW_STATUS_FAILURE';

// New action types for review canvas flow
export const START_REVIEW = 'START_REVIEW';
export const START_REVIEW_SUCCESS = 'START_REVIEW_SUCCESS';
export const START_REVIEW_FAILURE = 'START_REVIEW_FAILURE';
export const UPDATE_REVIEW_PROGRESS = 'UPDATE_REVIEW_PROGRESS';
export const TRACK_EDIT = 'TRACK_EDIT';
export const TRACK_EDIT_SUCCESS = 'TRACK_EDIT_SUCCESS';
export const TRACK_EDIT_FAILURE = 'TRACK_EDIT_FAILURE';
export const GENERATE_LLM_REVIEW = 'GENERATE_LLM_REVIEW';
export const GENERATE_LLM_REVIEW_SUCCESS = 'GENERATE_LLM_REVIEW_SUCCESS';
export const GENERATE_LLM_REVIEW_FAILURE = 'GENERATE_LLM_REVIEW_FAILURE';

/**
 * Submit an assessment for review
 * @param {string} modelId - Model UUID
 * @param {string} assessmentUuid - Assessment UUID
 * @returns {Function} Thunk action
 */
export const submitForReview = (modelId, assessmentUuid) => async (dispatch, getState) => {
  dispatch({ type: SUBMIT_FOR_REVIEW });

  try {
    const response = await authFetch('/.netlify/functions/manageAssessment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        modelId,
        assessmentUuid,
        operation: 'submitForReview'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to submit for review');
    }

    const result = await response.json();

    // Get user info from state if available (will be populated by Auth0)
    const state = getState();
    const userEmail = state.risks?.models?.[modelId]?.owner_email || '';
    const userName = state.risks?.models?.[modelId]?.owner_name || '';

    dispatch({
      type: SUBMIT_FOR_REVIEW_SUCCESS,
      payload: {
        modelId,
        assessmentUuid,
        reviewStatus: result.reviewStatus || 'submitted',
        requestedBy: {
          email: userEmail,
          name: userName,
          requestedAt: new Date().toISOString()
        }
      }
    });

    console.log('[reviewActions] Review submitted successfully:', { modelId, assessmentUuid });
    return result;
  } catch (error) {
    console.error('[reviewActions] Failed to submit for review:', error);
    dispatch({
      type: SUBMIT_FOR_REVIEW_FAILURE,
      payload: error.message
    });
    throw error;
  }
};

/**
 * Approve a review
 * @param {string} modelId - Model UUID
 * @param {string} assessmentUuid - Assessment UUID
 * @param {string} comments - Optional review comments
 * @returns {Function} Thunk action
 */
export const approveReview = (modelId, assessmentUuid, comments = '') => async (dispatch, getState) => {
  dispatch({ type: UPDATE_REVIEW_STATUS });

  try {
    const response = await authFetch('/.netlify/functions/manageAssessment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        modelId,
        assessmentUuid,
        operation: 'updateReviewStatus',
        reviewStatus: 'approved'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to approve review');
    }

    const result = await response.json();

    // Get reviewer info from result if available
    const reviewerEmail = result.reviewedBy || '';

    dispatch({
      type: UPDATE_REVIEW_STATUS_SUCCESS,
      payload: {
        modelId,
        assessmentUuid,
        reviewStatus: 'approved',
        reviewedBy: {
          email: reviewerEmail,
          reviewedAt: new Date().toISOString()
        },
        reviewResults: { comments }
      }
    });

    console.log('[reviewActions] Review approved successfully:', { modelId, assessmentUuid });
    return result;
  } catch (error) {
    console.error('[reviewActions] Failed to approve review:', error);
    dispatch({
      type: UPDATE_REVIEW_STATUS_FAILURE,
      payload: error.message
    });
    throw error;
  }
};

/**
 * Reject a review (for future use)
 * @param {string} modelId - Model UUID
 * @param {string} assessmentUuid - Assessment UUID
 * @param {string} comments - Rejection comments
 * @returns {Function} Thunk action
 */
export const rejectReview = (modelId, assessmentUuid, comments = '') => async (dispatch) => {
  dispatch({ type: UPDATE_REVIEW_STATUS });

  try {
    const response = await authFetch('/.netlify/functions/manageAssessment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        modelId,
        assessmentUuid,
        operation: 'updateReviewStatus',
        reviewStatus: 'rejected'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to reject review');
    }

    const result = await response.json();
    const reviewerEmail = result.reviewedBy || '';

    dispatch({
      type: UPDATE_REVIEW_STATUS_SUCCESS,
      payload: {
        modelId,
        assessmentUuid,
        reviewStatus: 'rejected',
        reviewedBy: {
          email: reviewerEmail,
          reviewedAt: new Date().toISOString()
        },
        reviewResults: { comments }
      }
    });

    console.log('[reviewActions] Review rejected:', { modelId, assessmentUuid });
    return result;
  } catch (error) {
    console.error('[reviewActions] Failed to reject review:', error);
    dispatch({
      type: UPDATE_REVIEW_STATUS_FAILURE,
      payload: error.message
    });
    throw error;
  }
};

/**
 * Start a review session
 * @param {string} modelId - Model UUID
 * @param {string} assessmentUuid - Assessment UUID
 * @returns {Function} Thunk action
 */
export const startReview = (modelId, assessmentUuid) => async (dispatch) => {
  dispatch({ type: START_REVIEW });

  try {
    const response = await authFetch('/.netlify/functions/manageReview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        modelId,
        assessmentUuid,
        operation: 'startReview'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to start review');
    }

    const result = await response.json();

    dispatch({
      type: START_REVIEW_SUCCESS,
      payload: {
        modelId,
        assessmentUuid,
        reviewStatus: result.reviewStatus || 'in_progress',
        startedBy: result.startedBy
      }
    });

    console.log('[reviewActions] Review started successfully:', { modelId, assessmentUuid });
    return result;
  } catch (error) {
    console.error('[reviewActions] Failed to start review:', error);
    dispatch({
      type: START_REVIEW_FAILURE,
      payload: error.message
    });
    throw error;
  }
};

/**
 * Update review progress
 * @param {string} modelId - Model UUID
 * @param {string} step - Current step ('risks', 'security', 'summary')
 * @param {string} itemId - Current item ID (risk or threat)
 * @returns {Object} Action
 */
export const updateReviewProgress = (modelId, step, itemId) => ({
  type: UPDATE_REVIEW_PROGRESS,
  payload: { modelId, step, itemId }
});

/**
 * Track an edit made during review (generic for all item types)
 * @param {string} modelId - Model UUID
 * @param {string} itemType - Type of item ('risk', 'control', 'residual_risk', 'threat', etc.)
 * @param {string} itemId - Item UUID
 * @param {string} field - Field name that was edited
 * @param {*} oldValue - Previous value
 * @param {*} newValue - New value
 * @param {Object} userInfo - User information { name, email, userId }
 * @returns {Function} Thunk action
 */
export const trackEdit = (modelId, itemType, itemId, field, oldValue, newValue, userInfo) => async (dispatch) => {
  // Optimistic update to Redux
  dispatch({
    type: TRACK_EDIT,
    payload: { modelId, itemType, itemId, field, oldValue, newValue, userInfo }
  });

  try {
    const response = await authFetch('/.netlify/functions/manageReview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        operation: 'trackEdit',
        modelId,
        itemType,
        itemId,
        field,
        oldValue,
        newValue,
        userInfo
      })
    });

    if (!response.ok) {
      throw new Error('Failed to track edit');
    }

    dispatch({
      type: TRACK_EDIT_SUCCESS,
      payload: { modelId, itemType, itemId }
    });

    console.log('[reviewActions] Edit tracked:', { itemType, itemId, field });
  } catch (error) {
    console.error('[reviewActions] Failed to track edit:', error);
    dispatch({
      type: TRACK_EDIT_FAILURE,
      payload: error.message
    });
    // Don't throw - edit tracking failure shouldn't block user
  }
};

/**
 * Generate LLM review for an item (risk, threat, etc.)
 * @param {string} modelId - Model UUID
 * @param {string} itemType - Type of item ('risk', 'threat', etc.)
 * @param {string} itemId - Item UUID
 * @param {string} customInstructions - Custom instructions for LLM
 * @returns {Function} Thunk action
 */
export const generateLLMReview = (modelId, itemType, itemId, customInstructions) => async (dispatch) => {
  dispatch({ type: GENERATE_LLM_REVIEW });

  try {
    const response = await authFetch('/.netlify/functions/generateRiskReview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        modelId,
        itemType,
        itemId,
        customInstructions
      })
    });

    if (!response.ok) {
      throw new Error('Failed to generate LLM review');
    }

    const feedback = await response.json();

    dispatch({
      type: GENERATE_LLM_REVIEW_SUCCESS,
      payload: { modelId, itemType, itemId, feedback, customInstructions }
    });

    console.log('[reviewActions] LLM review generated:', { itemType, itemId });
    return feedback;
  } catch (error) {
    console.error('[reviewActions] Failed to generate LLM review:', error);
    dispatch({
      type: GENERATE_LLM_REVIEW_FAILURE,
      payload: error.message
    });
    throw error;
  }
};
